import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../../AppContext";
import "./seasonCalendar.scss";

const message = (error) =>
  error.response?.data?.message || error.message || "Dayparts unavailable.";
const timePattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const DAYPART_TYPES = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "late_night_dinner", label: "Late Night Dinner" },
  { value: "suhoor", label: "Suhoor" },
  { value: "other", label: "Other" },
];

export const validateDaypart = ({ type, startTime, endTime }) => {
  if (!DAYPART_TYPES.some((option) => option.value === type))
    return "Select a daypart type.";
  if (!timePattern.test(startTime) || !timePattern.test(endTime))
    return "Enter valid times in HH:mm format.";
  if (startTime === endTime) return "Start and end times must be different.";
  return "";
};

function useDayparts(brandId) {
  const { user, chimeraserv } = useAppContext();
  const token = user?.data?.usrKey;
  const [revision, setRevision] = useState(0);
  const [list, setList] = useState({
    brandId: "",
    items: [],
    loading: false,
    error: "",
  });
  useEffect(() => {
    if (!brandId || !token) return undefined;
    const controller = new AbortController();
    setList({ brandId, items: [], loading: true, error: "" });
    axios
      .get(`${chimeraserv.urlGet}/dayparts/${encodeURIComponent(brandId)}`, {
        headers: { Authorization: token },
        signal: controller.signal,
      })
      .then(({ data }) => {
        const records = Array.isArray(data) ? data : data?.dayparts;
        if (!Array.isArray(records))
          throw new Error("Invalid dayparts response.");
        if (controller.signal.aborted) return;
        setList({
          brandId,
          items: records.map((item) => ({
            ...item,
            id: item.id ?? item._id,
            braKey: item.braKey ?? brandId,
          })),
          timeZone: data.timeZone || "Asia/Qatar",
          permissions: data.permissions ?? { edit: true },
          loading: false,
          error: "",
        });
      })
      .catch((failure) => {
        if (!controller.signal.aborted) {
          const notFound = failure.response?.status === 404;
          setList({
            brandId,
            items: [],
            loading: false,
            notFound,
            error: notFound
              ? "Dayparts list not found. You can create a daypart; listing requires GET /dayparts/:braKey."
              : message(failure),
          });
        }
      });
    return () => controller.abort();
  }, [brandId, token, chimeraserv.urlGet, revision]);
  return {
    ...(list.brandId === brandId
      ? list
      : { items: [], loading: !!brandId, error: "" }),
    reload: () => setRevision((value) => value + 1),
  };
}

export function DaypartTabs({ brandId, value, onChange }) {
  const list = useDayparts(brandId);
  return (
    <section className="menuGroupTabs menuDaypartTabs" aria-label="Dayparts">
      <div className="menuGroupTabRow">
        <h2 className="menuGroupTabLabel">Dayparts</h2>
        <div role="group" aria-label="Filter by daypart">
          <button
            type="button"
            className={!value ? "active" : ""}
            aria-pressed={!value}
            onClick={() => onChange(null)}
          >
            All dayparts
          </button>
          {list.items.map((item) => (
            <button
              type="button"
              key={item.id}
              disabled={!item.id}
              className={value === item.id ? "active" : ""}
              aria-pressed={value === item.id}
              onClick={() => onChange({ ...item, timeZone: list.timeZone })}
            >
              {DAYPART_TYPES.find((option) => option.value === item.type)
                ?.label ||
                (item.type === "late_night" ? "Late Night Dinner" : item.type)}
              {/* <span className="menuSeasonDates">{item.startTime} - {item.endTime}{item.endTime < item.startTime ? " (+1 day)" : ""}</span> */}
            </button>
          ))}
        </div>
      </div>
      {!brandId && <p role="status">Select one brand to view dayparts.</p>}
      {list.loading && <p role="status">Loading dayparts...</p>}
      {list.error && (
        <p role="alert">
          {list.error}{" "}
          <button type="button" onClick={list.reload}>
            Retry
          </button>
        </p>
      )}
      {brandId && !list.loading && !list.error && !list.items.length && (
        <p role="status">No dayparts available.</p>
      )}
    </section>
  );
}

export default function Dayparts() {
  const { user, chimeraserv } = useAppContext();
  const token = user?.data?.usrKey;
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [brandLoading, setBrandLoading] = useState(true);
  const [brandError, setBrandError] = useState("");
  const [brandRevision, setBrandRevision] = useState(0);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const current = useDayparts(brandId);
  const canEdit =
    !!brandId &&
    !brandLoading &&
    !brandError &&
    !current.loading &&
    (current.notFound || (!current.error && current.permissions?.edit));

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    setBrandLoading(true);
    setBrandError("");
    axios
      .get(`${chimeraserv.urlGet}/brand`, {
        headers: { Authorization: token },
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Invalid brand response.");
        if (controller.signal.aborted) return;
        setBrands(data);
        setBrandId((previous) => previous || String(data[0]?.braKey || ""));
        setBrandLoading(false);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) {
          setBrandError(message(failure));
          setBrandLoading(false);
        }
      });
    return () => controller.abort();
  }, [token, chimeraserv.urlGet, brandRevision]);

  const save = async (event) => {
    event.preventDefault();
    if (!canEdit || saving) return;
    const validation = validateDaypart(draft);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await axios[draft.id ? "put" : "post"](
        `${chimeraserv.urlPost}${draft.id ? `/daypart/${encodeURIComponent(draft.id)}` : "/daypart/create"}`,
        {
          braKey: brandId,
          type: draft.type.trim(),
          startTime: draft.startTime,
          endTime: draft.endTime,
        },
        {
          headers: { Authorization: token, "Content-Type": "application/json" },
        }
      );
      setDraft(null);
      setNotice("Daypart saved.");
      current.reload();
    } catch (failure) {
      setError(message(failure));
    } finally {
      setSaving(false);
    }
  };
  const field = (key) => ({
    value: draft[key],
    onChange: (event) => setDraft({ ...draft, [key]: event.target.value }),
  });

  return (
    <section className="seasonCalendar daypartSettings">
      <header>
        <h1>Dayparts</h1>
        <div className="seasonToolbar">
          <label>
            Brand
            <select
              aria-label="Brand"
              value={brandId}
              disabled={saving || brandLoading}
              onChange={(event) => {
                setBrandId(event.target.value);
                setDraft(null);
                setError("");
                setNotice("");
              }}
            >
              <option value="">
                {brandLoading ? "Loading brands..." : "Select brand"}
              </option>
              {brands.map((brand) => (
                <option key={brand.braKey} value={brand.braKey}>
                  {brand.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={!canEdit || saving}
            onClick={() => {
              setDraft({
                type: "breakfast",
                startTime: "07:00",
                endTime: "11:00",
              });
              setError("");
              setNotice("");
            }}
          >
            <FontAwesomeIcon icon={["fas", "plus"]} /> Add daypart
          </button>
        </div>
      </header>
      {brandError && (
        <p role="alert">
          {brandError}{" "}
          <button
            type="button"
            onClick={() => setBrandRevision((value) => value + 1)}
          >
            Retry
          </button>
        </p>
      )}
      {!brandLoading && !brandError && !brands.length && (
        <p role="status">No brands available.</p>
      )}
      {current.loading && <p role="status">Loading dayparts...</p>}
      {current.error && (
        <p role="alert">
          {current.error}{" "}
          <button type="button" onClick={current.reload}>
            Retry
          </button>
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {draft && (
        <form onSubmit={save} className="seasonEditor">
          <h2>{draft.id ? "Edit daypart" : "New daypart"}</h2>
          <fieldset disabled={saving || !canEdit} className="seasonFields">
            <label>
              Type
              <select required {...field("type")}>
                {DAYPART_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Start time
              <input required type="time" step="60" {...field("startTime")} />
            </label>
            <label>
              End time
              <input required type="time" step="60" {...field("endTime")} />
            </label>
          </fieldset>
          <div className="seasonActions">
            <button type="submit" disabled={saving || !canEdit}>
              <FontAwesomeIcon icon={["fas", "save"]} />{" "}
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setDraft(null);
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {brandId && !current.loading && !current.error && (
        <div className="seasonTableWrap">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Start time</th>
                <th>End time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {current.items.map((item, index) => (
                <tr key={item.id ?? index}>
                  <td>{item.type}</td>
                  <td>{item.startTime}</td>
                  <td>
                    {item.endTime}
                    {item.endTime < item.startTime && <small>Next day</small>}
                  </td>
                  <td>
                    {canEdit && (
                      <button
                        type="button"
                        disabled={saving || !item.id}
                        title="Edit daypart"
                        aria-label={`Edit ${item.type}`}
                        onClick={() => {
                          setDraft({
                            id: item.id,
                            type:
                              item.type === "late_night"
                                ? "late_night_dinner"
                                : item.type,
                            startTime: item.startTime,
                            endTime: item.endTime,
                          });
                          setError("");
                          setNotice("");
                        }}
                      >
                        <FontAwesomeIcon icon={["fas", "pen"]} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!current.items.length && <p>No dayparts for this brand.</p>}
        </div>
      )}
    </section>
  );
}
